import React, { ComponentType, useEffect, useState } from 'react';
import Router, { useRouter } from 'next/router';

import {
  CUSTOMER_EMAIL,
  CUSTOMER_ORDER,
  QUERY_CUSTOMER_EMAIL,
  QUERY_CUSTOMER_ORDER,
} from '../constants';

import customerDetails from '../customerDetails';

export type WithWaitForCredentialsProps = {
  isWaitingForCredentials: boolean;
};

/**
 * Given an expectedAuthenticationStatus and a redirectUrl, this wrapper redirects the user if
 * he is (resp.isn't) authenticated when he shouldn't (resp. should) be
 * @param WrappedComponent
 * @param expectedAuthenticationStatus set to `true` if the user should be authenticated, `false` if he shouldn't
 * @param redirectUrl
 * @returns
 */
const withWaitForCredentials = <T,>(
  WrappedComponent: ComponentType<T & WithWaitForCredentialsProps>,
  expectedAuthenticationStatus: boolean,
  redirectUrl: string,
) =>
  function WithWaitForCredentials(props: T) {
    const { query, asPath } = useRouter();
    const [isWaiting, setWaiting] = useState(true);

    useEffect(() => {
      if (query) {
        const customerEmail = query[QUERY_CUSTOMER_EMAIL]?.toString();
        const customerOrder = query[QUERY_CUSTOMER_ORDER]?.toString();

        if (customerEmail && customerOrder) {
          const currentlyLoggedInEmail = customerDetails.getCustomerEmail();

          const loggedWithADifferentAccount =
            currentlyLoggedInEmail !== '' &&
            customerEmail !== currentlyLoggedInEmail;

          if (loggedWithADifferentAccount) {
            customerDetails.clearCustomerDetails();
            // we reload the window to disconnect the current user and log him back with the credentials from the query
            // this way, we can make sure that no component is still reliant on stale authentication state
            window.location.href = asPath;
            return;
          }

          customerDetails.setCustomerDetails({
            [CUSTOMER_EMAIL]: customerEmail,
            [CUSTOMER_ORDER]: customerOrder,
          });
        }
      }
    }, [asPath, query]);

    useEffect(() => {
      const queryParams = new URLSearchParams(query as Record<string, string>);

      const isCustomerAuthenticated = customerDetails.isCustomerAuthenticated();

      if (isCustomerAuthenticated !== expectedAuthenticationStatus) {
        if (expectedAuthenticationStatus == true) {
          const encodedRedirectUrl = encodeURIComponent(asPath);
          let nextUrl = redirectUrl;

          if (encodedRedirectUrl) {
            nextUrl += `?redirect_to=${encodedRedirectUrl}`;
          }
          // Redirect to a login view so we need to provide a way get back
          Router.push(nextUrl);
          return;
        }

        if (queryParams.has('redirect_to')) {
          const decodedRedirectUrl = decodeURIComponent(
            queryParams.get('redirect_to') as string,
          );
          Router.push(decodedRedirectUrl);
          return;
        }

        if (queryParams.toString()) {
          Router.push(`${redirectUrl}?${queryParams.toString()}`);
        } else {
          Router.push(redirectUrl);
        }
      } else {
        setWaiting(false);
      }
    }, [query, asPath]);

    return React.createElement(WrappedComponent, {
      isWaitingForCredentials: isWaiting,
      ...props,
    });
  };

export default withWaitForCredentials;
